import type { RosterRow, Student, StudentGender } from "./types.js";

type StudentNumber = { number: number; sourceClass?: string };

export function studentRosterKey(student: StudentNumber): string {
  return JSON.stringify([student.sourceClass ?? "", student.number]);
}

export function studentNumberLabel(student: StudentNumber, padded = false): string {
  const number = padded ? String(student.number).padStart(2, "0") : String(student.number);
  return student.sourceClass ? `${student.sourceClass}（${number}）` : number;
}

export function compareStudentNumbers(a: StudentNumber, b: StudentNumber): number {
  return (a.sourceClass ?? "").localeCompare(b.sourceClass ?? "", "en", { numeric: true }) || a.number - b.number;
}

export function isValidSourceClass(value: unknown): value is string | undefined {
  return (
    value === undefined ||
    (typeof value === "string" && value.length > 0 && value.length <= 40 && value === normalizeRosterClass(value))
  );
}

export function nextStudentNumber(students: readonly Pick<Student, "number">[]): number | undefined {
  const used = new Set(students.map((student) => student.number));
  const next = Math.max(0, ...used) + 1;
  if (next <= 999) return next;
  return Array.from({ length: 999 }, (_, index) => index + 1).find((number) => !used.has(number));
}

export function validateNewStudent(
  row: { number: number; name: string; gender: string; sourceClass?: string },
  students: readonly StudentNumber[],
): RosterRow {
  if (!Number.isInteger(row.number) || row.number < 1 || row.number > 999) throw new Error("invalid-student-number");
  const name = row.name.trim();
  if (!name) throw new Error("missing-student-name");
  if (!["", "M", "F", "X"].includes(row.gender)) throw new Error("invalid-student-gender");
  if (!isValidSourceClass(row.sourceClass)) throw new Error("invalid-source-class");
  if (students.some((student) => studentRosterKey(student) === studentRosterKey(row)))
    throw new Error("duplicate-student-number");
  return {
    number: row.number,
    name,
    gender: row.gender as StudentGender,
    ...(row.sourceClass ? { sourceClass: row.sourceClass } : {}),
  };
}

function genderValue(value: string): StudentGender {
  const normalized = value.trim().toLowerCase();
  if (["m", "男", "male"].includes(normalized)) return "M";
  if (["f", "女", "female"].includes(normalized)) return "F";
  if (["x", "其他", "other"].includes(normalized)) return "X";
  return "";
}

type RosterColumnMapping = {
  numberColumn: number;
  nameColumn: number;
  fallbackNameColumn?: number;
  genderColumn?: number;
  hasHeader: boolean;
};

export class RosterParseError extends Error {
  constructor(
    readonly code: "format" | "columns" | "gender" | "duplicate" | "empty" | "class",
    readonly line = 1,
    readonly studentNumber = 0,
  ) {
    super(
      code === "format"
        ? `第 ${line} 行格式不正確。`
        : code === "duplicate"
          ? `班號 ${studentNumber} 重複。`
          : `roster-${code}`,
    );
  }
}

function rosterCells(line: string): string[] | undefined {
  if (!/[,，\t]/.test(line))
    return line
      .trim()
      .split(/\s{2,}/)
      .map((cell) => cell.trim());
  // Excel columns take priority: commas inside a tab-separated name are literal.
  const delimiter = line.includes("\t") ? /\t/ : /[,，]/;
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < line.length; index++) {
    const character = line.charAt(index);
    if (character === '"' && (quoted || !cell.trim())) {
      if (quoted && line[index + 1] === '"') {
        cell += '"';
        index++;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && delimiter.test(character)) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += character;
    }
  }
  if (quoted) return undefined;
  cells.push(cell.trim());
  return cells;
}

function readRosterTable(input: string): string[][] {
  return (
    input
      .replace(/^\uFEFF/, "")
      .split(/\r\n|\n|\r/)
      // Keep leading/trailing tabs: empty Excel cells still occupy a column.
      .filter((line) => line.trim())
      .map((line, index) => {
        const cells = rosterCells(line);
        if (!cells) throw new RosterParseError("format", index + 1);
        return cells;
      })
  );
}

function headerColumn(cells: readonly string[], labels: readonly string[]): number {
  const matches = cells.flatMap((cell, index) =>
    labels.includes(cell.toLowerCase().replace(/[\s._（）()]/g, "")) ? [index] : [],
  );
  return matches.length === 1 ? (matches[0] ?? -1) : matches.length ? -2 : -1;
}

function detectRosterColumns(table: readonly (readonly string[])[]): RosterColumnMapping {
  const first = table[0] ?? [];
  const numberHeader = headerColumn(first, [
    "班號",
    "班內編號",
    "班中編號",
    "學號",
    "序號",
    "classno",
    "classnumber",
    "no",
    "number",
    "studentno",
  ]);
  const chineseHeader = headerColumn(first, ["中文姓名", "中文名", "chinesename", "nameinchinese", "chname"]);
  const englishLabels = ["英文姓名", "英文名", "englishname", "nameinenglish", "enname"];
  const englishHeader = headerColumn(first, englishLabels);
  const nameHeader =
    chineseHeader !== -1
      ? chineseHeader
      : headerColumn(first, ["姓名", "學生姓名", "name", "studentname", ...englishLabels]);
  const genderHeader = headerColumn(first, ["性別", "gender", "sex"]);
  if (numberHeader !== -1 && nameHeader !== -1) {
    return {
      numberColumn: numberHeader >= 0 ? numberHeader : -1,
      nameColumn: nameHeader >= 0 ? nameHeader : -1,
      fallbackNameColumn:
        chineseHeader >= 0 && table.slice(1).some((row) => !row[chineseHeader]) && englishHeader !== -1
          ? englishHeader >= 0
            ? englishHeader
            : -1
          : undefined,
      genderColumn: genderHeader >= 0 ? genderHeader : genderHeader === -2 ? -1 : undefined,
      hasHeader: true,
    };
  }

  const width = table.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  const numberColumns: number[] = [];
  const genderColumns: number[] = [];
  const nameColumns: number[] = [];
  const chineseColumns: number[] = [];
  for (let column = 0; column < width; column++) {
    const values = table.map((row) => row[column] ?? "");
    if (!values.some(Boolean)) continue;
    if (values.every((value) => /^\d{1,3}$/.test(value) && Number(value) >= 1 && Number(value) <= 999)) {
      numberColumns.push(column);
    } else if (values.every((value) => !value || genderValue(value))) {
      genderColumns.push(column);
    } else if (values.every((value) => !value || (/\p{L}/u.test(value) && !/\p{N}/u.test(value)))) {
      nameColumns.push(column);
      if (values.every((value) => !value || /\p{Script=Han}/u.test(value))) chineseColumns.push(column);
    }
  }
  const names = chineseColumns.length ? chineseColumns : nameColumns;
  const nameColumn = names.length === 1 ? (names[0] ?? -1) : -1;
  const fallbackColumns = nameColumns.filter((column) => column !== nameColumn);
  const needsFallback = chineseColumns.length === 1 && table.some((row) => !row[nameColumn]);
  return {
    numberColumn: numberColumns.length === 1 ? (numberColumns[0] ?? -1) : -1,
    nameColumn,
    fallbackNameColumn:
      needsFallback && fallbackColumns.length ? (fallbackColumns.length === 1 ? fallbackColumns[0] : -1) : undefined,
    genderColumn: genderColumns.length === 1 ? genderColumns[0] : genderColumns.length > 1 ? -1 : undefined,
    hasHeader: false,
  };
}

export type RosterTableMapping = Omit<RosterColumnMapping, "hasHeader"> & { headerRow: number; classColumn?: number };

export function detectRosterTable(rows: readonly (readonly string[])[], headerRow?: number): RosterTableMapping {
  const normalized = rows.map((row) => row.map((cell) => cell.trim()));
  const detectedHeader = headerRow ?? normalized.findIndex((row) => detectRosterColumns([row]).hasHeader);
  const table = normalized.slice(detectedHeader < 0 ? 0 : detectedHeader).filter((row) => row.some(Boolean));
  const { hasHeader: _, ...mapping } = detectRosterColumns(table);
  const classColumn =
    detectedHeader < 0
      ? -1
      : headerColumn(normalized[detectedHeader] ?? [], ["classcode", "class", "classname", "班別", "班級"]);
  return {
    ...mapping,
    headerRow: detectedHeader,
    classColumn: classColumn === -1 ? undefined : classColumn === -2 ? -1 : classColumn,
  };
}

export function normalizeRosterClass(value: string): string {
  return value.normalize("NFKC").replace(/\s+/g, "").toUpperCase();
}

export function rosterTableClasses(rows: readonly (readonly string[])[], mapping: RosterTableMapping): string[] {
  if (mapping.classColumn === undefined || mapping.classColumn < 0) return [];
  const column = mapping.classColumn;
  return [
    ...new Set(
      rows
        .slice(mapping.headerRow + 1)
        .map((row) => normalizeRosterClass(row[column] ?? ""))
        .filter(Boolean),
    ),
  ];
}

export function suggestRosterClass(classes: readonly string[], className: string): string {
  const candidates = [normalizeRosterClass(className), normalizeRosterClass(className.trim().split(/\s+/)[0] ?? "")];
  return candidates.find((candidate) => classes.includes(candidate)) ?? "";
}

export function parseRosterTable(
  rows: readonly (readonly string[])[],
  mapping: RosterTableMapping,
  classCode?: string | null,
): RosterRow[] {
  const width = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  const selected = [
    mapping.numberColumn,
    mapping.nameColumn,
    mapping.fallbackNameColumn,
    mapping.genderColumn,
    mapping.classColumn,
  ].filter((column): column is number => column !== undefined);
  if (
    !Number.isInteger(mapping.headerRow) ||
    mapping.headerRow < -1 ||
    mapping.headerRow >= rows.length ||
    new Set(selected).size !== selected.length ||
    selected.some((column) => !Number.isInteger(column) || column < 0 || column >= width)
  )
    throw new RosterParseError("columns");
  const classes = rosterTableClasses(rows, mapping);
  const crossClass = classCode === null;
  if (crossClass && mapping.classColumn === undefined) throw new RosterParseError("class");
  const sourceClass =
    classCode == null ? (classes.length === 1 ? (classes[0] ?? "") : "") : normalizeRosterClass(classCode);
  if (!crossClass && mapping.classColumn !== undefined && (!sourceClass || !classes.includes(sourceClass)))
    throw new RosterParseError("class");
  const result: RosterRow[] = [];
  const numbers = new Set<string>();
  for (let index = mapping.headerRow + 1; index < rows.length; index++) {
    const cells = rows[index]?.map((cell) => cell.trim()) ?? [];
    if (!cells.some(Boolean)) continue;
    let rowClass: string | undefined;
    if (mapping.classColumn !== undefined) {
      rowClass = normalizeRosterClass(cells[mapping.classColumn] ?? "");
      if (!rowClass || !isValidSourceClass(rowClass)) throw new RosterParseError("format", index + 1);
      if (!crossClass && rowClass !== sourceClass) continue;
    }
    const rawNumber = cells[mapping.numberColumn] ?? "";
    const number = Number(rawNumber);
    const name =
      cells[mapping.nameColumn] ||
      (mapping.fallbackNameColumn === undefined ? "" : cells[mapping.fallbackNameColumn]) ||
      "";
    const rawGender = mapping.genderColumn === undefined ? "" : (cells[mapping.genderColumn] ?? "");
    const gender = genderValue(rawGender);
    if (!/^\d{1,3}$/.test(rawNumber) || number < 1 || number > 999 || !name)
      throw new RosterParseError("format", index + 1);
    if (rawGender && !gender) throw new RosterParseError("gender", index + 1);
    const row: RosterRow = { number, name, gender, ...(crossClass ? { sourceClass: rowClass } : {}) };
    const key = studentRosterKey(row);
    if (numbers.has(key)) throw new RosterParseError("duplicate", index + 1, number);
    numbers.add(key);
    result.push(row);
  }
  if (!result.length) throw new RosterParseError("empty");
  return result.sort(compareStudentNumbers);
}

export function parseRosterTextForClass(input: string, className: string, crossClass = false): RosterRow[] {
  const table = readRosterTable(input);
  const mapping = detectRosterTable(table);
  if (crossClass) return parseRosterTable(table, mapping, null);
  if (mapping.classColumn === undefined) return parseRosterText(input);
  return parseRosterTable(table, mapping, suggestRosterClass(rosterTableClasses(table, mapping), className));
}

export function parseRosterText(input: string): RosterRow[] {
  const table = readRosterTable(input);
  if (!table.length) return [];
  const width = table[0]?.length ?? 0;
  const detected = detectRosterColumns(table);
  const columns = input.includes("\t") || detected.hasHeader ? detected : undefined;
  if (columns?.hasHeader && table.length === 1) throw new RosterParseError("format", 2);
  if (columns) {
    const selected = [
      columns.numberColumn,
      columns.nameColumn,
      ...(columns.fallbackNameColumn === undefined ? [] : [columns.fallbackNameColumn]),
      ...(columns.genderColumn === undefined ? [] : [columns.genderColumn]),
    ];
    if (
      new Set(selected).size !== selected.length ||
      selected.some((column) => !Number.isInteger(column) || column < 0 || column >= width)
    ) {
      throw new RosterParseError("columns");
    }
  }
  const rows = table.slice(columns?.hasHeader ? 1 : 0).map((cells, index) => {
    const line = index + (columns?.hasHeader ? 2 : 1);
    if (columns ? cells.length !== width : cells.length > 3) throw new RosterParseError("format", line);
    const first = cells[0] ?? "";
    const compactMatch = first.match(/^(\d{1,3})\s+(.+?)(?:\s+([MF男女X]))?$/i);
    const number = Number(columns ? cells[columns.numberColumn] : cells.length > 1 ? first : compactMatch?.[1]);
    const name = String(
      columns
        ? cells[columns.nameColumn] ||
            (columns.fallbackNameColumn === undefined ? "" : cells[columns.fallbackNameColumn])
        : cells.length > 1
          ? cells[1]
          : (compactMatch?.[2] ?? ""),
    ).trim();
    const rawGender = String(
      columns
        ? columns.genderColumn === undefined
          ? ""
          : cells[columns.genderColumn]
        : cells.length > 2
          ? cells[2]
          : (compactMatch?.[3] ?? ""),
    );
    const gender = genderValue(rawGender);
    if (!Number.isInteger(number) || number < 1 || number > 999 || !name) {
      throw new RosterParseError("format", line);
    }
    if (columns && rawGender.trim() && !gender) throw new RosterParseError("gender", line);
    return { number, name, gender } satisfies RosterRow;
  });

  const seen = new Set<number>();
  for (const row of rows) {
    if (seen.has(row.number)) throw new RosterParseError("duplicate", 1, row.number);
    seen.add(row.number);
  }
  return rows.sort((a, b) => a.number - b.number);
}
